import { Link } from "react-router-dom";
import { Quote, Film, Image as ImageIcon, ShieldCheck, Tag, MapPin, MessageCircle, User, Pencil, Trash2 } from "lucide-react";
import { fileUrl } from "../lib/api";

const CONSENT_LABEL = {
  approved_named: { label: "Named", color: "#7ac143" },
  approved_anonymized: { label: "Anonymized", color: "#0091be" },
  not_yet_asked: { label: "Pending consent", color: "#f68b1f" },
};

export default function StoryCard({ story, canEdit = false, canDelete = false, onEdit, onDelete }) {
  const hasQuote = !!(story.client_quote && story.client_quote.trim());
  const media = story.media || [];
  const hasImage = media.some((m) => m.kind === "image");
  const hasVideo = media.some((m) => m.kind === "video");
  const consent = CONSENT_LABEL[story.consent_status] || CONSENT_LABEL.not_yet_asked;
  const isClientTold = story.source === "client";
  const sourceLabel = isClientTold ? "Client testimonial" : "Rep entered";
  const SourceIcon = isClientTold ? MessageCircle : User;
  const sourceColor = isClientTold ? "#0091be" : "#64748B";

  return (
    <div className="relative group">
      <Link
        to={`/story/${story.id}`}
        data-testid={`story-card-${story.id}`}
        className="tc-card p-6 flex flex-col gap-3 hover:border-slate-300 group/card"
      >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {story.client_logo_path && (
            <img
              src={fileUrl(story.client_logo_path)}
              alt="Client logo"
              data-testid={`story-card-logo-${story.id}`}
              className="h-8 w-8 rounded-lg object-contain bg-white border border-slate-200 p-0.5 shrink-0"
            />
          )}
          <div className="tc-pill bg-slate-100 text-slate-700">
            <Tag size={12} /> {story.industry || "Industry,"}
          </div>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: consent.color }}>
          {consent.label}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 -mt-1">
        {(story.state || story.country) && (
          <span className="tc-pill bg-slate-50 text-slate-600 border border-slate-200" data-testid={`story-card-location-${story.id}`}>
            <MapPin size={11} /> {[story.state, story.country].filter(Boolean).join(", ")}
          </span>
        )}
        {story.facilitator && (
          <span className="tc-pill bg-slate-50 text-slate-600 border border-slate-200" data-testid={`story-card-facilitator-${story.id}`}>
            w/ {story.facilitator}
          </span>
        )}
        <span
          className="tc-pill border"
          style={{ background: `${sourceColor}1A`, color: sourceColor, borderColor: `${sourceColor}33` }}
          data-testid={`story-card-source-${story.id}`}
          title={isClientTold ? "Submitted directly by the client" : "Captured by the sales rep"}
        >
          <SourceIcon size={11} /> {sourceLabel}
        </span>
      </div>

      <div>
        {(story.company_name || story.client_submitter_org) && (
          <div className="text-[11px] uppercase tracking-widest font-extrabold text-orange-600 mb-0.5" data-testid={`story-card-company-${story.id}`}>
            {story.company_name || story.client_submitter_org}
          </div>
        )}
        <h3 className="font-display text-xl text-tc-ink leading-snug line-clamp-2" data-testid={`story-card-title-${story.id}`}>
          {story.title || story.challenge_text || "Untitled story"}
        </h3>
        {story.title && story.challenge_text && (
          <>
            <div className="tc-eyebrow text-slate-400 mt-2">Challenge</div>
            <p className="text-sm text-slate-700 line-clamp-2 leading-relaxed mt-0.5">{story.challenge_text}</p>
          </>
        )}
      </div>

      <div>
        <div className="tc-eyebrow text-slate-400">Outcome</div>
        <p className="text-sm text-slate-700 line-clamp-2 leading-relaxed mt-0.5">{story.after_text || ","}</p>
      </div>

      <div className="mt-auto flex items-center gap-2 pt-3 border-t border-slate-100">
        {hasQuote && (
          <span className="tc-pill bg-yellow-50 text-yellow-800" title="Has client quote"><Quote size={12} /> Quote</span>
        )}
        {hasImage && (
          <span className="tc-pill bg-blue-50 text-blue-700" title="Has image"><ImageIcon size={12} /> Photo</span>
        )}
        {hasVideo && (
          <span className="tc-pill bg-orange-50 text-orange-700" title="Has video"><Film size={12} /> Video</span>
        )}
        {story.status === "approved" && (
          <span className="tc-pill bg-green-50 text-green-700 ml-auto"><ShieldCheck size={12} /> Approved</span>
        )}
        {story.status === "pending" && (
          <span className="tc-pill bg-slate-100 text-slate-700 ml-auto">In review</span>
        )}
        {story.status === "draft" && (
          <span className="tc-pill bg-slate-100 text-slate-700 ml-auto">Draft</span>
        )}
        {story.status === "rejected" && (
          <span className="tc-pill bg-red-50 text-red-700 ml-auto">Needs edits</span>
        )}
      </div>
    </Link>
      {(canEdit || canDelete) && (
        <div
          className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
          data-testid={`story-card-admin-${story.id}`}
        >
          {canEdit && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit?.(story); }}
              data-testid={`story-card-edit-${story.id}`}
              title="Edit story"
              className="w-8 h-8 rounded-lg bg-white border border-slate-200 shadow-sm inline-flex items-center justify-center text-slate-500 hover:text-tc-ink hover:bg-slate-50"
            >
              <Pencil size={13} />
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete?.(story); }}
              data-testid={`story-card-delete-${story.id}`}
              title="Delete story"
              className="w-8 h-8 rounded-lg bg-white border border-red-200 shadow-sm inline-flex items-center justify-center text-red-500 hover:bg-red-50"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
